/**
 * The session JWT lives in an HttpOnly cookie, so nothing here can read it and an
 * XSS payload cannot steal it either. The browser attaches it to every API call on
 * its own, which is why requests only have to opt into sending credentials.
 *
 * What this file does hold is the CSRF companion token. It cannot come from
 * document.cookie: the API is served from a different host than this portal, and a
 * cookie set by one host is not visible to a page on another. The API therefore
 * returns the token in the login response, we keep it here, and state changing
 * requests echo it back in a header. The API compares that header against its own
 * cookie, which the browser keeps sending automatically.
 *
 * Keeping it in localStorage is not a weakness: the token is useless without the
 * HttpOnly session cookie, and a CSRF token has never been a defence against script
 * running inside the page.
 */
export const CSRF_STORAGE_KEY = 'csrfToken';
export const CSRF_HEADER_NAME = 'x-csrf-token';

const STATE_CHANGING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

function storage(): Storage | null {
    // guards server side rendering and privacy modes that deny storage access
    try {
        return typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
        return null;
    }
}

/**
 * Read the CSRF token issued with the current session. Returns null before login, or
 * once the session has been cleared.
 */
export function readCsrfToken(): string | null {
    return storage()?.getItem(CSRF_STORAGE_KEY) ?? null;
}

/** Remember the token the API returned in the login response. */
export function storeCsrfToken(token: string | null | undefined): void {
    if (!token) {
        return;
    }
    storage()?.setItem(CSRF_STORAGE_KEY, token);
}

export function clearCsrfToken(): void {
    storage()?.removeItem(CSRF_STORAGE_KEY);
}

export function requiresCsrfToken(method: string): boolean {
    return STATE_CHANGING_METHODS.includes((method || '').toUpperCase());
}
